import React, { useReducer } from 'react';
import { Routes, Route, BrowserRouter } from 'react-router-dom';

import { DispatchContext } from '../../contexts/DispatchContext';
import { StateContext } from '../../contexts/StateContext';
import { initializer } from '../../reducer/initializer';
import { reducer } from '../../reducer/reducer';
import { Session } from '../../types/Session';
import { NotFound } from '../NotFound/NotFound';
import { ViewPage } from './pages/ViewPage';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { Toaster } from 'react-hot-toast';
import { Container } from './components/Container';
import {Reservations} from "./pages/Reservations";

interface Props {
  session: Session;
  data: any;
}

export const ServiceProviderPages: React.ComponentType<Props> = ({
  session,
  data,
}) => {
  const [state, dispatch] = useReducer(reducer, session, initializer);

  const theme = createTheme({
    palette: {
      primary: {
        main: '#2c65de',
      },
      secondary: {
        main: '#256861',
      },
    },
    typography: {
      h1: {
        fontSize: '2rem',
        '@media (max-width:600px)': {
          fontSize: '1.5rem',
        },
      },
      h2: {
        fontSize: '1.75rem',
        '@media (max-width:600px)': {
          fontSize: '1.3rem',
        },
      },
      h3: {
        fontSize: '1.5rem',
        '@media (max-width:600px)': {
          fontSize: '1.15rem',
        },
      },
      h4: {
        fontSize: '1.25rem',
        '@media (max-width:600px)': {
          fontSize: '1.0rem',
        },
      },

      h5: {
        fontSize: '1.15rem',
        '@media (max-width:600px)': {
          fontSize: '0.95rem',
        },
      },
      h6: {
        fontSize: '1rem',
        '@media (max-width:600px)': {
          fontSize: '0.85rem',
        },
      },

      body1: {
        fontSize: '1rem',
        '@media (max-width:600px)': {
          fontSize: '0.875rem',
        },
      },
      body2: {
        fontSize: '0.875rem',
        '@media (max-width:600px)': {
          fontSize: '0.7rem',
        },
      },
      caption: {
        fontSize: '0.7rem',
        '@media (max-width:600px)': {
          fontSize: '0.6rem',
        },
      },
    },
    components: {
      MuiButtonBase: {
        styleOverrides: {
          root: {
            '@media (max-width:600px)': {
              padding: '5px',
              fontSize: '11px',
              textTransform: 'none',
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            '@media (max-width:600px)': {
              minHeight: '32px',
              padding: '5px',
              fontSize: '11px',
              textTransform: 'none',
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            '@media (max-width:600px)': {
              minHeight: '32px',
              padding: '5px',
              fontSize: '11px',
            },
          },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            '@media (max-width:600px)': {
              minHeight: '32px !important',
            },
          },
          content: {
            '@media (max-width:600px)': {
              margin: '5px 0px !important',
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            '@media (max-width:600px)': {
              minHeight: '32px',
            },
          },
          indicator: {
            '@media (max-width:600px)': {
              height: '2px',
            },
          },
        },
      },
    },
  });

  return (
    <DispatchContext.Provider value={dispatch}>
      <StateContext.Provider value={state}>
        <ThemeProvider theme={theme}>
          <BrowserRouter>
            <Routes>
              <Route path="" element={<Container />}>
                <Route index element={<ViewPage data={data} />} />
                <Route path="/:id/:action" element={<ViewPage data={data} />} />
                <Route path="/reservations" element={<Reservations data={data} />} />
                <Route path="*" element={<NotFound session={state} />} />
              </Route>
            </Routes>
            <Toaster position="bottom-center" />
          </BrowserRouter>
        </ThemeProvider>
      </StateContext.Provider>
    </DispatchContext.Provider>
  );
};
